import type {
	CarEnergy$options,
	Currency$options,
	DistanceSystem$options,
	ItineraryConstraint$options,
	ItineraryTrafficMode$options,
	ItineraryTravelMode$options,
} from '$houdini';
import type { BoundaryCoordinates, Coordinates } from '$lib/components/map/types';
import type { SearchAddressEntityType } from '$lib/components/search/address/types';
import type { WayPoint } from '$lib/components/search/itinerary/types';
import type { RoutesIds } from '$lib/constants/route';
import type { SupportedLocale } from '@viamichelin/business';
import { dev } from '$app/environment';
import { goto } from '$app/navigation';
import { resolve } from '$app/paths';
import {
	currenciesList,
	distanceSystemsList,
	itineraryTrafficMap,
	travelModesList,
	tripConstraintsList,
} from '$lib/components/itinerary/constant';
import { DEFAULT_LEVEL_OF_CHARGE, type TripOptionCar, tripOptionsCarName } from '$lib/components/itinerary/tripOptions';
import { POI_FILTER_ACCOMMODATION_RATE_DEFAULT_VALUE, poiCategoryBits } from '$lib/components/pois/constants';
import {
	type MainPoiCategory,
	mainPoiCategoryList,
	type PoiCategory,
	poiCategoryList,
	type PoiFilterId,
	type PoiFilterValue,
} from '$lib/components/pois/types';
import { DEFAULT_DISTANCE_KM } from '$lib/components/zoi/zoiSettings';
import { DEFAULT_ACCOMMODATION_AVAILABILITY_END_DATES_DAYS_DIFF, DEFAULT_ACCOMMODATION_AVAILABILITY_START_DATES_DAYS_DIFF } from '$lib/constants/searchParams';
import { getLanguageCode } from '$lib/i18n/language';
import { i18nPath } from '$lib/i18n/routing';
import { isCorridorPoiListPage, isRouteResultsPage } from '$lib/services/urls/routeIds';
import { timeZonesByLocale } from '$lib/utils/date';
import { getLocalTimeZone, today } from '@internationalized/date';
import { error } from '@sveltejs/kit';
import { getContext, setContext } from 'svelte';
import { get } from 'svelte/store';
import { queryParameters, ssp } from 'sveltekit-search-params';

export interface SelectedPoi {
	id: string;
	type: PoiCategory;
}

export interface SelectedAddress {
	address: string;
	entityType?: SearchAddressEntityType;
	coordinates?: Coordinates;
	// Are the bounds of the map synced with the bounds of the selected address. Useful for SEO purposes.
	boundsSync?: boolean;
	// Address details for the selected address
	// These are used for SEO purposes and tracking for advertising
	region?: string;
	zipCode?: string;
	countryCode?: string;
	city?: string;
}

export interface CorridorParams {
	category?: PoiCategory;
	radius?: number;
}

type ModalNames = 'poland' | 'login';
type GotoOptions = Parameters<typeof goto>[1];

// separator for array values in params, ~ doesn't need url encoding and does not occur in numbers or simple strings.
const sep = '~';

/*
 * Possible address types in the URL
 *
 * The order of this array should be treated carefully as any modification will break the existing
 * URLs
 */
const addressTypes = ['COUNTRY', 'COUNTRY_SUBDIVISION', 'COUNTRY_SUBDIVISION_1', 'CITY'] satisfies SearchAddressEntityType[];

export interface SearchParams {
	/** pagination page number for poilist */
	page: number;
	/** map bounds */
	bounds?: BoundaryCoordinates;
	/** map center */
	center?: Coordinates;
	/** selected poi categories for home page */
	poiCategories?: MainPoiCategory[];
	/** selected poi category for poi corridor */
	corridor?: CorridorParams;
	/** selected address */
	selectedAddress?: SelectedAddress;
	/** selected poi id and category */
	selectedPoi?: SelectedPoi;
	/** list of itinerary waypoint coordinates including departure/arrival */
	itinerary?: WayPoint[];
	/** id of selected car for itinerary */
	car?: TripOptionCar;
	/** travel mode for itinerary */
	travelMode?: ItineraryTravelMode$options;
	/** constraint for itinerary */
	tripConstraint?: ItineraryConstraint$options;
	/** distance unit for itinerary */
	distanceSystem?: DistanceSystem$options;
	/** energy price for itinerary */
	energyPrice?: number;
	/** currency for itinerary */
	currency?: Currency$options;
	/** selected filters to display pois */
	filters?: Partial<Record<PoiFilterId, PoiFilterValue>>;
	/** selected route */
	selectedRoute?: number;
	/** selected zoi to search POIs */
	zoi?: {
		center: Coordinates;
		radius: number;
	};
	/** settings used to fetch ZOIs */
	zoiSettings: { containsEvOnly?: boolean; distance: number };
	/** charging level alert */
	rechargeTreshold?: number;
	/** activate caravan option for itinerary */
	withCaravan?: boolean;
	/** traffic options used for itinerary */
	traffic?: ItineraryTrafficMode$options;
	/** used for users redirected from .pl domain or when showing login modal when a user try to access the profile directly */
	showModal?: ModalNames;
	/** used to redirect user's arriving on profile pages to the original page after login action completed */
	redirectUserTo?: string;
}

interface SearchParamOption<T = any> {
	encode: (value: T) => string;
	decode: (value: string | null) => T | null;
	defaultValue?: T | undefined;
}
type SearchParamsRecord = Record<keyof SearchParams, SearchParamOption>;

export const searchParamsOptions: SearchParamsRecord = {
	page: {
		...ssp.number(),
		defaultValue: 1,
	},
	poiCategories: {
		encode(value: MainPoiCategory[]) {
			const bitmask
				= value?.reduce((result, category) => result + (poiCategoryBits[category] ?? 0), 0) ?? 0;
			return bitmask.toString(16);
		},
		decode(str): MainPoiCategory[] | null {
			if (str == null) {
				return null;
			}
			const bitmask = Number.parseInt(str, 16);
			return Number.isNaN(bitmask)
				? []
				: mainPoiCategoryList.filter((value: MainPoiCategory) => bitmask & poiCategoryBits[value]);
		},
	},
	corridor: {
		encode(value: CorridorParams) {
			return [value.category ?? '', value.radius ?? ''].join(sep);
		},
		decode(str) {
			if (str == null) {
				return str;
			}
			const parts = str.split(sep);
			if (parts.length !== 2) {
				return null;
			}
			const category = poiCategoryList.includes(parts[0] as PoiCategory)
				? (parts[0] as PoiCategory)
				: undefined;
			const radius = Number.parseFloat(parts[1]);
			return { category, radius: Number.isNaN(radius) ? undefined : radius };
		},
	},
	bounds: {
		encode(value: BoundaryCoordinates) {
			return value?.flat().join(sep);
		},
		decode(str) {
			if (str == null) {
				return str;
			}
			const parts = str.split(sep).map(s => Number.parseFloat(s));
			return [
				[parts[0], parts[1]],
				[parts[2], parts[3]],
			];
		},
	},
	center: {
		encode(value: Coordinates) {
			return [value.lng, value.lat].join(sep);
		},
		decode(str) {
			if (str == null) {
				return str;
			}
			const lngLat = str.split(sep).map(s => Number.parseFloat(s));
			return { lng: lngLat[0], lat: lngLat[1] };
		},
	},
	selectedAddress: ssp.object(),
	selectedPoi: {
		encode(value: SelectedPoi) {
			return [value.id, value.type].join(sep);
		},
		decode(str) {
			if (str == null) {
				return str;
			}
			const idType = str.split(sep);
			if (idType.length !== 2 || !poiCategoryList.includes(idType[1] as PoiCategory)) {
				return null;
			}
			return { id: idType[0], type: idType[1] as PoiCategory };
		},
	},
	itinerary: {
		encode: (waypoints: WayPoint[]) => {
			return waypoints
				.map(wp =>
					JSON.stringify({
						t: wp.addressType ? addressTypes.indexOf(wp.addressType) : undefined,
						l: wp.label,
						c: wp.coordinates,
						cc: wp.countryCode,
						r: wp.region,
						p: wp.zipCode,
						isMyLoc: wp.isMyLocation || undefined,
						isArrival: wp.isArrival || undefined,
					}),
				)
				.join(sep);
		},
		decode: (value: string | null) => {
			if (value === null || value === '' || value === undefined) {
				return [];
			}

			const stringifiedWaypoints = value.split(sep);
			try {
				return stringifiedWaypoints.map((stringWp, index) => {
					const wp = JSON.parse(stringWp);
					return {
						index,
						id: index,
						label: wp.l,
						coordinates: wp.c,
						countryCode: wp.cc,
						region: wp.r,
						zipCode: wp.p,
						isMyLocation: wp.isMyLoc,
						isArrival: wp.isArrival,
						addressType: wp.t !== undefined ? addressTypes[wp.t] : undefined,
					};
				});
			} catch (e) {
				if (dev) {
					console.error(`failed to parse itinerary query param ${value}`, e);
				}
				error(400, 'invalid param itinerary');
			}
		},
	},
	car: {
		encode(car: TripOptionCar) {
			return [car.id, car.model, car.isDefault, car.isEv, car.energy, car.maker].join(sep);
		},
		decode(value): TripOptionCar | null {
			if (value == null) {
				return value;
			}
			const carValues = value.split(sep);

			// We support length 5 for users using the previous searchParam car value
			// that does not contain car maker
			// Can remove length 5 when, in analytics, most visited url does not have this url format anymore
			if (carValues.length !== 6 && carValues.length !== 5) {
				return null;
			}

			let carModel = carValues[1];

			// Condition aimed to support previous value format: model = `${car.model} - ${car.energy}`
			// Can remove this condition when, in analytics, most visited url does not have this url format anymore
			if (carModel.includes(tripOptionsCarName.separator)) {
				carModel = tripOptionsCarName.parse(carValues[1]).model;
			}

			return {
				id: carValues[0],
				model: carModel,
				isDefault: carValues[2] === 'true',
				isEv: carValues[3] === 'true',
				energy: carValues[4] as CarEnergy$options,
				maker: carValues[5],
			};
		},
	},
	travelMode: {
		...ssp.string(),
		decode(str) {
			if (!travelModesList.includes(str as ItineraryTravelMode$options)) {
				return null;
			}
			return str as ItineraryTravelMode$options;
		},
	},
	tripConstraint: {
		...ssp.string(),
		decode(str) {
			if (!tripConstraintsList.includes(str as ItineraryConstraint$options)) {
				return null;
			}
			return str as ItineraryConstraint$options;
		},
	},
	distanceSystem: {
		...ssp.string(),
		decode(str) {
			if (!distanceSystemsList.includes(str as DistanceSystem$options)) {
				return null;
			}
			return str as DistanceSystem$options;
		},
	},
	energyPrice: ssp.number(),
	currency: {
		...ssp.string(),
		decode(str) {
			if (!currenciesList.includes(str as Currency$options)) {
				return null;
			}
			return str as Currency$options;
		},
	},
	filters: {
		...ssp.object(),
		defaultValue: {
			ACCOMMODATION_RATE: POI_FILTER_ACCOMMODATION_RATE_DEFAULT_VALUE,
		},
	},
	selectedRoute: ssp.number(),
	zoi: ssp.object(),
	zoiSettings: {
		encode(value: { containsEvOnly: boolean; distance: number }) {
			return [value.containsEvOnly, value.distance].join(sep);
		},
		decode(value) {
			if (value == null) {
				return null;
			}

			const zoiSettingsValues = value.split(sep);

			if (zoiSettingsValues.length !== 2) {
				return null;
			}

			const zoiSettingsDistance = zoiSettingsValues[1] && Number(zoiSettingsValues[1]);

			return {
				containsEvOnly: zoiSettingsValues[0] === 'true',
				distance: Number.isNaN(zoiSettingsDistance) ? undefined : zoiSettingsDistance,
			};
		},
		defaultValue: {
			distance: DEFAULT_DISTANCE_KM,
		},
	},
	rechargeTreshold: { ...ssp.number(), defaultValue: DEFAULT_LEVEL_OF_CHARGE },
	withCaravan: ssp.boolean(),
	traffic: {
		...ssp.string(),
		decode(str) {
			return str ? itineraryTrafficMap[str] || null : null;
		},
	},
	showModal: ssp.string(),
	redirectUserTo: ssp.string(),
};

const defaultSearchParamValues: Record<string, any> = {};
Object.entries(searchParamsOptions).forEach(([key, config]) => {
	if (config.defaultValue != null) {
		defaultSearchParamValues[key] = config.defaultValue;
	}
});

export function parseSearchParams(usp: URLSearchParams, options: {
	hasPartnerAdinmap: boolean;
} = { hasPartnerAdinmap: false }): SearchParams {
	const { hasPartnerAdinmap } = options;

	try {
		return {
			...defaultSearchParamValues,
			...Object.fromEntries(
				[...usp.entries()].map(([paramKey, value]) => {
					let paramValue: unknown = value;

					const paramsOption = searchParamsOptions[paramKey as keyof SearchParamsRecord];
					if (paramsOption) {
						paramValue = paramsOption.decode(paramValue as string | null) ?? paramsOption.defaultValue;
						if (!hasPartnerAdinmap && paramKey === 'poiCategories' && Array.isArray(paramValue)) {
							paramValue = (paramValue as MainPoiCategory[]).filter((poiCategory: MainPoiCategory) => poiCategory !== 'PARTNER' && poiCategory !== 'PARTNER2');
						}
					}
					return [paramKey, paramValue];
				}),
			),
		} as SearchParams;
	} catch (e) {
		if (dev) {
			console.error(`failed to parse query params: ${usp.toString()} `, e);
		}
		error(400, 'invalid param');
	}
}

export type SearchParamsType = Partial<SearchParams> & Record<string, unknown>;

export function serializeSearchParams(searchParams: SearchParamsType) {
	const usp = new URLSearchParams();
	for (const [key, value] of Object.entries(searchParams)) {
		if (value == null) {
			continue;
		}
		// @ts-expect-error index access
		const option = searchParamsOptions[key];

		// There may be search parameters in the URL that are not in the options.
		// In this case use the value as is.
		if (!option) {
			usp.set(key, value as string);
		} else {
			// only add search param if value is not default
			if (option.defaultValue == null || option.defaultValue !== value) {
				usp.set(key, option.encode(value));
			}
		}
	}
	usp.sort();
	return usp.toString().replace(/%7E/g, '~'); // for some reason usp encodes it
}

export function gotoRelativeUrl(relativeUrl: string, options?: GotoOptions) {
	// using getLanguageCode on the server could be dangerous but goto is only ever called on the client
	return goto(resolve(i18nPath(relativeUrl, getLanguageCode()) as any), options);
}

export async function gotoWithParams(route: RoutesIds, searchParams?: SearchParamsType, options?: GotoOptions) {
	const search = serializeSearchParams(searchParams ?? {});
	const url = search ? `${route}?${search}` : route;
	return gotoRelativeUrl(url, options);
}

/**
 * amount of time before we determine history.back has failed
 */
const GOBACK_TIMEOUT = 500;

/**
 * use browser history.back unless we are on the first page of the session
 * on the first page, use gotoWithParams with the provided fallback values
 */
export async function goBackWithFallback(
	route: RoutesIds,
	searchParams?: Partial<SearchParams>,
	count: number = 1,
) {
	if (history.state.goback_use_fallback) {
		return gotoWithParams(route, searchParams);
	} else {
		// we want to go back with history.back, but goto should resolve only after navigation is done.
		// history.back is async and completes with popstate event, so we resolve when that happens
		// we can't know for sure it will, so in order to not keep it unresolved forever
		// use a timeout and abort if we don't get the popstate event in time
		let resolvePopstate: (value: unknown) => void;
		const popstate = new Promise((resolve) => {
			resolvePopstate = resolve;
		});
		const timeout = new Promise((_resolve, reject) => {
			setTimeout(reject, GOBACK_TIMEOUT);
		});
		// @ts-expect-error use before define (it's set by popstate promise)
		window.addEventListener('popstate', resolvePopstate);
		const backNav = Promise.race([popstate, timeout])
			.then(() => {})
			.finally(() => {
				window.removeEventListener('popstate', resolvePopstate);
			});
		history.go(-count);
		return backNav;
	}
}

export type SearchParamsStore = ReturnType<typeof queryParameters<SearchParams>>;

function createSearchParamsStore(pushHistory = true): SearchParamsStore {
	return queryParameters(searchParamsOptions, {
		sort: true,
		showDefaults: false,
		pushHistory,
	});
}

const storeKey = Symbol('searchParams');
const noHistoryStoreKey = Symbol('noHistorySearchParams');

export function getSearchParamsStore(pushHistory = true, locale?: SupportedLocale, route?: { id: string | null }): SearchParamsStore {
	const key = pushHistory ? storeKey : noHistoryStoreKey;
	let store = getContext<SearchParamsStore>(key);

	if (!store) {
		// using the defaultValue in searchParamsOptions.filters will not create the default filter for all routes
		// so we need to set it here for a minimal impact on the code. It will be easy to remove later.
		store = withDefaultFilters(createSearchParamsStore(pushHistory), locale, route);
		setContext<SearchParamsStore>(key, store);
	}

	return store;
}

export function setSearchParamOnUrlPath(path: string, key: string, value: string) {
	const url = new URL(path, 'https://example.com');
	url.searchParams.set(key, value);
	return url.pathname + url.search;
}

export function deleteSearchParamFromUrlPath(path: string, key: string) {
	const url = new URL(path, 'https://example.com');
	url.searchParams.delete(key);
	return url.pathname + url.search;
}

const searchParamsKeysVM = [...Object.keys(searchParamsOptions), 'from', 'to'];

export function removeAllVMSearchParamsFromUrl(url: string | URL) {
	const currentUrl = new URL(url.toString());

	for (const key of searchParamsKeysVM) {
		if (currentUrl.searchParams.has(key)) {
			currentUrl.searchParams.delete(key);
		}
	}

	return currentUrl.toString();
}

export function getSearchParamsFiltersWithDefault(currentFilters: SearchParams['filters'], locale?: SupportedLocale, route?: { id: string | null }): SearchParams['filters'] {
	const timeZone = locale ? timeZonesByLocale?.[locale] : getLocalTimeZone();
	const todayDate = today(timeZone);

	return {
		// This is a workaround to apply the default filters
		// This should be removed after the refacto on the management of the search params and the url state
		...((!currentFilters?.ACCOMMODATION_TYPE && route && (isCorridorPoiListPage(route) || isRouteResultsPage(route))) && { ACCOMMODATION_TYPE: ['HOTEL'] }),
		...(!currentFilters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE
			? {
					ACCOMMODATION_AVAILABILITY_DATE_RANGE: {
						startDate: todayDate.add({ days: DEFAULT_ACCOMMODATION_AVAILABILITY_START_DATES_DAYS_DIFF }).toString(),
						endDate: todayDate.add({ days: DEFAULT_ACCOMMODATION_AVAILABILITY_END_DATES_DAYS_DIFF }).toString(),
					},
				}
			: {}),
	};
}

export function withDefaultFilters(spStore: SearchParamsStore, locale?: SupportedLocale, route?: { id: string | null }): SearchParamsStore {
	const sp = get(spStore);

	if (sp.poiCategories?.includes('ACCOMMODATION') || sp?.corridor?.category === 'ACCOMMODATION') {
		const defaultFilters = getSearchParamsFiltersWithDefault(sp.filters, locale, route);
		spStore.update(searchParams => ({
			...searchParams,
			filters: {
				...searchParams.filters,
				...defaultFilters,
			},
		}));
	}

	return spStore;
}
